import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0, n2 } from "../../lib/format";
import { Notice, Stat } from "../../lib/ui";

/*
 * Stock reconciliation report – port of stockrecon_report.php (API: GET /api/stock/reconciliation-report, backend/stock2.py)
 * Admin only. Defaults to this month. Value impact = difference × purchase price. Max 1000 rows.
 */

const signed = (v, f = n0) => `${v > 0 ? "+" : ""}${f(v)}`;
const tone = (v) => (v > 0 ? "var(--up)" : v < 0 ? "var(--danger)" : "var(--muted)");

export default function ReconReport() {
  const api = useApi();
  const [f, setF] = useState({ start_date: "", end_date: "", variance: "all", search: "" });
  const [form, setForm] = useState(f);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async (q) => {
    try {
      const d = await api.get("/api/stock/reconciliation-report", q);
      setData(d); setForm(d.filters); setError(null);
    } catch (e) {
      if (/restricted to administrators/.test(e.message)) setDenied(true); else setError(e.message);
    }
  }, [api]);
  useEffect(() => { load(f); }, [load, f]);

  if (denied) {
    return (
      <main className="pg" style={{ maxWidth: "40rem" }}>
        <div className="panel empty" style={{ marginTop: "3rem" }}>
          <i className="fas fa-lock" style={{ color: "var(--danger)" }} />
          <h1 style={{ fontSize: "1.2rem", color: "var(--ink)", margin: "0 0 .4rem" }}>Admin access required</h1>
          <p style={{ margin: "0 0 1.2rem" }}>The reconciliation report is restricted to administrators.</p>
          <Link to="/dashboard" className="btn primary">Back to dashboard</Link>
        </div>
      </main>
    );
  }

  const k = data?.kpi;
  return (
    <main className="pg">
      <PageHeader slug="stockrecon_report" actions={<button type="button" className="btn" onClick={() => window.print()}><i className="fas fa-print" /> Print</button>} />
      <Notice notice={error && { tone: "err", text: error }} />
      <div className="panel no-print" style={{ marginBottom: "1.25rem" }}>
        <form className="toolbar" style={{ borderBottom: 0 }} onSubmit={(e) => { e.preventDefault(); setF({ ...form }); }}>
          <div className="field"><label htmlFor="rr-f">From</label><input id="rr-f" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
          <div className="field"><label htmlFor="rr-t">To</label><input id="rr-t" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          <div className="field"><label htmlFor="rr-v">Variance</label>
            <select id="rr-v" value={form.variance} onChange={(e) => setForm({ ...form, variance: e.target.value })}>
              <option value="all">All</option><option value="over">Overage (+)</option><option value="short">Shortage (−)</option><option value="matched">Matched (0)</option>
            </select></div>
          <div className="field grow"><label htmlFor="rr-q">Product or barcode</label>
            <input id="rr-q" value={form.search} onChange={(e) => setForm({ ...form, search: e.target.value })} placeholder="e.g. Coca Cola or barcode" /></div>
          <button type="submit" className="btn primary">Apply</button>
          <button type="button" className="btn" title="Reset filters" onClick={() => setF({ start_date: "", end_date: "", variance: "all", search: "" })}><i className="fas fa-rotate-left" /></button>
        </form>
      </div>
      {data && <p className="print-only" style={{ fontSize: ".8rem", color: "var(--muted)" }}>Range: {data.filters.start_date} to {data.filters.end_date}</p>}

      {k && (
        <div className="ui-stats">
          <Stat icon="fa-clipboard-check" label={`Reconciliations · ${n0(k.products_reconciled)} products`} value={n0(k.total_recon)} />
          <Stat icon="fa-scale-unbalanced" tone={k.net_qty_variance < 0 ? "danger" : "up"} label="Net quantity variance" value={signed(k.net_qty_variance)} unit="units" />
          <Stat icon="fa-arrows-up-down" tone="warn" label="Overage / shortage" value={`+${n0(k.total_overage_qty)} / −${n0(k.total_shortage_qty)}`} />
          <Stat icon="fa-sack-dollar" tone="danger" label={`Value at risk · net ${signed(k.net_value_impact, n2)}`} value={n2(k.value_at_risk)} unit="TZS" />
        </div>
      )}

      <section className="panel">
        <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", gap: "1rem" }}>
          <h2 style={{ fontSize: "1rem", margin: 0 }}>Itemized reconciliations</h2>
          {data && <span style={{ fontSize: ".8rem", color: "var(--muted)" }}>{data.rows.length} record{data.rows.length === 1 ? "" : "s"}{data.capped ? " (capped at 1000, narrow your filters)" : ""}</span>}
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Date</th><th>Product</th><th>Barcode</th><th className="num">Original</th><th className="num">Updated</th>
              <th className="num">Difference</th><th className="num">Value impact</th><th>Reconciled by</th><th>Remarks</th></tr></thead>
            <tbody>
              {data?.rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{r.reconciliation_date}</td>
                  <td><div style={{ fontWeight: 600, color: "var(--ink)" }}>{r.product_name}</div>
                    {(r.manufacturer || r.product_type) && <div style={{ fontSize: ".75rem", color: "var(--muted)" }}>{[r.manufacturer, r.product_type].filter(Boolean).join(" · ")}</div>}</td>
                  <td className="mono" style={{ fontSize: ".78rem" }}>{r.barcode}</td>
                  <td className="num mono">{n0(r.original_quantity)}</td><td className="num mono">{n0(r.updated_quantity)}</td>
                  <td className="num mono" style={{ color: tone(r.difference), fontWeight: 600 }}>{signed(r.difference)}</td>
                  <td className="num mono" style={{ color: tone(r.impact) }}>{signed(r.impact, n2)}</td>
                  <td>{r.reconciled_by || "–"}</td><td style={{ color: "var(--muted)" }}>{r.remarks}</td>
                </tr>
              ))}
            </tbody>
            {data?.rows.length > 0 && (
              <tfoot><tr><td colSpan={5}>Totals</td>
                <td className="num mono" style={{ color: tone(k.net_qty_variance) }}>{signed(k.net_qty_variance)}</td>
                <td className="num mono" style={{ color: tone(k.net_value_impact) }}>{signed(k.net_value_impact, n2)}</td><td colSpan={2} /></tr></tfoot>
            )}
          </table>
          {data && data.rows.length === 0 && <div className="empty"><i className="fas fa-inbox" />No reconciliation records found for this range.</div>}
        </div>
      </section>
    </main>
  );
}
